<template>
  <v-row>
    <v-col md="3" cols="12">
      <h2 class="primary--text font-weight-bold pt-5 pl-2">
        {{ $t('display/offer:create.display') }}
      </h2>
      <v-list-item three-line>
        <v-list-item-content>
          <v-list-item-title>{{ $t('display/offer:create.data-requirements') }} :</v-list-item-title>
          <v-list-item-subtitle>
            {{ $t('common:image') }}: 1280 x 720 px / .jpg {{ $t('display/offer:create.or') }} .png (RGB)
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            {{ $t('display/offer:create.video') }}: 1280x720 / .mp4 (H264 codec)
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            {{ $t('display/offer:create.need-help-text') }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-col>
    <v-col md="9" cols="12">
      <h2 class="primary--text font-weight-bold pt-5 pl-2">
        {{ $t('display/offer:create.summary') }}:
      </h2>
      <v-row cols="12">
        <v-col class="pr-0 pb-0 pt-0">{{ $t('display/offer:create.selected-locations') }}: {{selectedElevatorCount}}</v-col>
      </v-row>
      <v-row>
        <v-col class="pr-0 pb-0 pt-0">{{ $t('display/offer:create.overall-monthly-playouts') }}: {{playouts}}</v-col>
      </v-row>
      <v-row>
        <v-col class="pr-0 pb-0 pt-0">{{ $t('display/offer:create.keywords-tags') }}: {{selectedTags}}</v-col>
      </v-row>
      <v-row>
        <v-col class="pr-0 pb-0 pt-0">{{ $t('display/offer:create.manually-excluded-location') }}: {{excludedCount}}
          <v-btn
            icon
            color="pink"
            v-if="excludedCount > 0"
            @click="$emit('emptyExcluded')"
          >
            <v-icon>remove_circle_outline</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ma-2"
              :loading="checkAvailability"
              :disabled="checkAvailability"
              color="secondary"
              @click="checkOfferAvailability"
              v-bind="attrs"
              v-on="on"
            >
              {{$t('display/offer:create.check-availability')}}
            </v-btn>
          </template>
          <span>{{$t('display/offer:create.verify-campaign')}}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ma-2"
              :loading="downloadLoad"
              :disabled="downloadDisable"
              color="secondary"
              @click="$refs.stepGeneratePdf.showDialog = true"
              v-bind="attrs"
              v-on="on"
            >
              {{$t('display/offer:create.download-summary')}}
            </v-btn>
          </template>
          <span>{{$t('display/offer:create.download-tooltip')}}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ma-2"
              :loading="downloadLoad"
              :disabled="downloadDisable"
              color="secondary"
              @click="$emit('placeOffer')"
              v-bind="attrs"
              v-on="on"
            >
              {{ $t('display/offer:create.step.place') }}
            </v-btn>
          </template>
          <span>{{$t('display/offer:create.place-campaign')}}</span>
        </v-tooltip>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-alert
            outlined
            type="error"
            v-if="!errorText !== true"
          >{{ errorText }}
          </v-alert>
        </v-col>
      </v-row>
    </v-col>

    <TwoButtonDialog
      ref="stepGeneratePdf"
      :dialog-title="$t('display/offer:overview.dialog.pdf-title')"
      :dialog-text="$t('display/offer:overview.dialog.pdf-text')"
      :button1-text="$t('buttons:cancel')"
      :button2-text="$t('display/offer:overview.dialog.send')"
      :hideButton="true"
      @clickedButton1="$refs.stepGeneratePdf.showDialog = false"
      @clickedButton2="generatedPdf"
    />

  </v-row>
</template>

<script>

import Utils from '@/modules/core/services/utils';
import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog';
import OfferService from '@/modules/display/services/offer-service';
import PdfService from '@/modules/display/services/pdf-service';

export default {
  name: "ThirdStep",
  components: {
    TwoButtonDialog
  },
  data() {
    return {
      results: null,
      excludedCount: 0,
      selectedElevatorCount: 0,
      selectedTags: '',
      playouts: '',
      checkAvailability: false,
      downloadLoad: false,
      downloadDisable: true,
      sendData: null,
      errorText: ''
    };
  },
  methods: {
    clear() {
      this.results = null;
      this.excludedCount = 0;
      this.selectedElevatorCount = 0;
      this.selectedTags = '';
      this.playouts = '';
      this.checkAvailability = false;
      this.downloadLoad = false;
      this.downloadDisable = true;
      this.sendData = null;
      this.errorText = '';
    },
    setResults(results) {
      this.results = results;
      this.excludedCount = results.excludedElevators.length;
      this.selectedElevatorCount = results.selectedElevators.length;
      this.selectedTags = results.selectedTagsWithName;
      this.playouts = this.getPlayouts(results.selectedElevators);
    },
    checkOfferAvailability() {
      if (!this.couldBeSend()) {
        Utils.showFailureSnackbar(this.$t('display/offer:create.errors.no-elevators'));
        return;
      }

      this.sendData = Object.assign(this.results.model,
        {regions: this.results.selectedRegions},
        {excludedElevators: this.results.excludedElevators},
        {tags: this.getTagsId(this.results.selectedTags)},
        {buildingTypes: this.getTagsId(this.results.selectedBuildings)}
      );

      OfferService.checkAvailability(this.sendData,
        (response) => {
          let errors = response.data;
          this.errorText = '';

          if (errors === null || errors.length === 0) {
            this.downloadDisable = false;
            this.$emit('updateNextButton', this.sendData);
            return;
          }

          let errorMessage = '';

          for (let i = 0; i < errors.length; i++) {
            errorMessage += this.$t(`display/offer:create.errors.${errors[i].error}`, {errorValue: errors[i].value}) + " ";
          }

          this.errorText = errorMessage;

        },
        () => {
          Utils.showFailureSnackbar(this.$t('display/offer:create.errors.no-elevators'));
        }
      )

    },
    getTagsId(tags) {
      if (typeof tags === 'undefined' || tags === null || tags.length <= 0) {
        return [];
      }
      let tagIds = [];
      tags.forEach(
          (item) => {
            tagIds.push(item);
          }
      );
      return tagIds;
    },
    couldBeSend() {
      return this.selectedElevatorCount > 0;
    },
    downloadGeneratedPdf(pdfDocumentId) {
      PdfService.downloadPdf(
        pdfDocumentId,
        (pdfFile) => {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(pdfFile);
          link.download = pdfDocumentId + '.pdf';
          link.click();
          URL.revokeObjectURL(link.href);
        }
      )
    },
    generatedPdf() {
      this.$refs.stepGeneratePdf.setButtonSettings(false, true, true, false);
      OfferService.sendPdfGenerationRequestByObject(
        this.sendData,
        (response) => {
          let pdfId = response.data.pdfFileId;
          this.checkIfPdfGenerated(pdfId);
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('display/offer:overview.snackbar.pdf')}));
        }
      )
    },
    checkIfPdfGenerated(pdfDocumentId) {
      PdfService.sendPdfCheckStatusRequest(
        pdfDocumentId,
        (response) => {
          if (response.status == 202) {
            this.checkIfPdfGenerated(pdfDocumentId);
          } else {
            this.$refs.stepGeneratePdf.showDialog = false;
            this.$refs.stepGeneratePdf.setButtonSettings(false, false, false, false);
            this.downloadGeneratedPdf(pdfDocumentId);
          }
        },
        () => {
          this.$refs.stepGeneratePdf.showDialog = false;
          this.$refs.stepGeneratePdf.setButtonSettings(false, false, false, false);
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('display/offer:overview.snackbar.pdf')}));
        }
      )
    },
    getPlayouts(elevators) {
      let playouts = 0;

      elevators.forEach(
        (elevator) => {
          playouts += elevator.playouts;
        }
      );

      return playouts > 0 ? playouts : this.$t('display/offer:create.table.not-enough-data');
    },
    previousStep() {
      this.downloadDisable = true;
    },
    couldContinue() {
      return true;
    }
  }
}
</script>


<style scoped>

</style>
